// ProfitOfThePeriod.js
import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale } from 'chart.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

ChartJS.register(Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale);

// Dummy data
const allData = {
  monthly: {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    data: [1000, 1200, 1100, 1300, 1400, 1200, 1600, 1700, 1500, 1800, 1700, 1900],
  },
  weekly: {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    data: [1200, 1500, 1800, 2200],
  },
  yearly: {
    labels: ['2020', '2021', '2022', '2023'],
    data: [15000, 16000, 17000, 18000],
  },
  custom: {
    labels: [], // Will be updated dynamically
    data: [],   // Will be updated dynamically
  },
};

function ProfitOfThePeriod() {
  const [range, setRange] = useState('monthly');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [customData, setCustomData] = useState({ labels: [], data: [] });

  useEffect(() => {
    if (range === 'custom') {
      // Simulate custom data fetching/filtering based on startDate and endDate
      const filteredData = {
        labels: ['Custom 1', 'Custom 2', 'Custom 3'], // Replace with actual date range labels
        data: [1200, 1500, 1700], // Replace with actual data based on date range
      };
      setCustomData(filteredData);
    }
  }, [startDate, endDate, range]);

  const handleRangeChange = (selectedRange) => {
    setRange(selectedRange);
  };

  const data = {
    labels: range === 'custom' ? customData.labels : allData[range].labels,
    datasets: [
      {
        label: 'Profit',
        data: range === 'custom' ? customData.data : allData[range].data,
        fill: false,
        backgroundColor: '#0284c7',
        borderColor: '#0284c7',
        borderWidth: 3,
        pointBackgroundColor: '#ffffff',
        pointBorderColor: '#0284c7',
        pointBorderWidth: 2,
        pointRadius: 4,
        tension: 0.35,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            return `$${tooltipItem.formattedValue}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: '#64748b' },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: { color: '#64748b' },
        grid: { color: '#e2e8f0' },
      },
    },
  };

  return (
    <div className="h-full rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <h3 className="text-lg font-bold text-slate-950">Profit of the Period</h3>
          <p className="text-sm text-slate-500">Revenue performance by selected range</p>
        </div>
        <div className="rounded-lg bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 ring-1 ring-emerald-100">
          +8.4%
        </div>
      </div>

      <div className="mb-5 flex flex-wrap gap-2 rounded-lg bg-slate-50 p-1.5">
        <button
          className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${range === 'monthly' ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500 hover:text-slate-950'}`}
          onClick={() => handleRangeChange('monthly')}
        >
          Monthly
        </button>
        <button
          className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${range === 'weekly' ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500 hover:text-slate-950'}`}
          onClick={() => handleRangeChange('weekly')}
        >
          Weekly
        </button>
        <button
          className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${range === 'yearly' ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500 hover:text-slate-950'}`}
          onClick={() => handleRangeChange('yearly')}
        >
          Yearly
        </button>
        <button
          className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${range === 'custom' ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500 hover:text-slate-950'}`}
          onClick={() => handleRangeChange('custom')}
        >
          Custom
        </button>
      </div>
      {range === 'custom' && (
        <div className="mb-5 grid gap-4 sm:grid-cols-2">
          <label className="block text-sm font-semibold text-slate-700">
            Start Date
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              dateFormat="yyyy-MM-dd"
              className="mt-1 block h-10 w-full rounded-lg border border-slate-200 px-3 text-sm font-normal shadow-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
            />
          </label>
          <label className="block text-sm font-semibold text-slate-700">
            End Date
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              dateFormat="yyyy-MM-dd"
              className="mt-1 block h-10 w-full rounded-lg border border-slate-200 px-3 text-sm font-normal shadow-sm outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
            />
          </label>
        </div>
      )}
      <div className="h-72">
        <Line data={data} options={options} />
      </div>
    </div>
  );
}

export default ProfitOfThePeriod;
